import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NETWORK_COST_OPERATION_TYPES } from '@entities/reconciliation';
import type { ReconciliationFilterOption } from '@features/reconciliation';
import type { ComplianceReviewer } from '@shared/api/contracts/compliance';
import { Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/ui';

const ALL = '__all__';

type FilterSelectProps = {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
};

export function ReconciliationFilterSelect({
  id,
  label,
  value,
  onChange,
  options,
  disabled = false,
}: FilterSelectProps) {
  const { t } = useTranslation('cabinet');

  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value || ALL}
        disabled={disabled}
        onValueChange={(next) => onChange(next === ALL ? undefined : next)}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={t('reconciliation.filters.all')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('reconciliation.filters.all')}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type FilterInputProps = {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  inputMode?: 'text' | 'numeric';
  disabled?: boolean;
};

export function ReconciliationFilterInput({
  id,
  label,
  value,
  onChange,
  inputMode = 'text',
  disabled = false,
}: FilterInputProps) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value ?? ''}
        inputMode={inputMode}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value.trim();
          onChange(next || undefined);
        }}
      />
    </div>
  );
}

type AssigneeFilterProps = {
  id: string;
  label: string;
  value?: number;
  assignees: ComplianceReviewer[];
  onChange: (value: number | undefined) => void;
};

type AssetNetworkFilterProps = {
  id: string;
  label: string;
  value?: string;
  options: ReconciliationFilterOption[];
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
};

export function ReconciliationAssetNetworkFilter({
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
}: AssetNetworkFilterProps) {
  if (options.length === 0) {
    return (
      <ReconciliationFilterInput id={id} label={label} value={value} onChange={onChange} disabled={disabled} />
    );
  }

  return (
    <ReconciliationFilterSelect
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled}
    />
  );
}

export function ReconciliationAssigneeFilter({ id, label, value, assignees, onChange }: AssigneeFilterProps) {
  const useSelect = assignees.length > 0;

  if (!useSelect) {
    return (
      <ReconciliationFilterInput
        id={id}
        label={label}
        inputMode="numeric"
        value={value != null ? String(value) : ''}
        onChange={(next) => onChange(next ? Number(next) : undefined)}
      />
    );
  }

  return (
    <ReconciliationFilterSelect
      id={id}
      label={label}
      value={value != null ? String(value) : undefined}
      onChange={(next) => onChange(next ? Number(next) : undefined)}
      options={assignees.map((admin) => ({
        value: String(admin.id),
        label: admin.email,
      }))}
    />
  );
}

export function useReconciliationFilterOptions() {
  const { t } = useTranslation('cabinet');

  const runStatusOptions = ['QUEUED', 'RUNNING', 'COMPLETE', 'PARTIAL', 'FAILED'].map((value) => ({
    value,
    label: t(`reconciliation.runStatus.${value}`),
  }));

  const caseStatusOptions = ['OPEN', 'INVESTIGATING', 'RESOLVED'].map((value) => ({
    value,
    label: t(`reconciliation.caseStatus.${value}`),
  }));

  const caseTypeOptions = [
    'LEDGER_DIFFERENCE',
    'BALANCE_MISMATCH',
    'OPENING_GAP',
    'SUSPENSE_MISMATCH',
  ].map((value) => ({
    value,
    label: t(`reconciliation.caseType.${value}`),
  }));

  const suspenseStatusOptions = ['UNIDENTIFIED', 'ASSIGNED', 'SETTLED'].map((value) => ({
    value,
    label: t(`reconciliation.suspenseStatus.${value}`),
  }));

  const costStateOptions = [
    'PREPARED',
    'CHARGED',
    'AWAITING_EXECUTION',
    'SETTLING',
    'SETTLED',
    'MANUAL_REVIEW',
    'CANCELLED',
  ].map((value) => ({
    value,
    label: t(`reconciliation.costState.${value}`, { defaultValue: value }),
  }));

  const operationTypeOptions = NETWORK_COST_OPERATION_TYPES.map((value) => ({
    value,
    label: t(`reconciliation.policies.operation.${value}`, { defaultValue: value }),
  }));

  return {
    runStatusOptions,
    caseStatusOptions,
    caseTypeOptions,
    suspenseStatusOptions,
    costStateOptions,
    operationTypeOptions,
  };
}

export function ReconciliationFilterFieldGroup({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
